"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { usePublicIntegrations } from "@/components/shared/PublicIntegrations";

interface SidebarBookingFormProps {
  buttonText?: string;
  title?: string;
  eyebrow?: string;
}

export function SidebarBookingForm({ buttonText, title, eyebrow }: SidebarBookingFormProps = {}) {
  const { prepareEnquiryContext, trackLeadSubmission } = usePublicIntegrations();
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "+91",
    city: "",
    age: "",
    consent: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formState.name.trim()) return "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email.trim())) return "Please enter a valid email address.";
    if (!/^\d{10}$/.test(formState.phone)) return "Please enter a valid 10-digit phone number.";
    if (!formState.age || Number(formState.age) < 10 || Number(formState.age) > 110) return "Please enter a valid age.";
    return "";
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");

    const validationError = validateForm();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      const integrationContext = await prepareEnquiryContext("sidebar-booking");
      const response = await fetch("/api/enquiries", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...formState, ...integrationContext }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Submission failed");
      trackLeadSubmission("sidebar-booking", result.id);
      window.location.href = "/thankyou";
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Please call the clinic to book your appointment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative w-full overflow-hidden rounded-[2rem] bg-brand-orange p-7 text-white shadow-xl sm:p-9">
      <div className="absolute inset-0 brand-cell-pattern opacity-[0.1] pointer-events-none" />

      <AnimatePresence mode="wait">
        <motion.div
          key="form"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
        >
            <p className="font-accent text-xs uppercase text-white/80 mb-2">
              {eyebrow || "Consult with our experts"}
            </p>
            <h3 className="text-xl font-semibold text-white">
              {title || "Book Free Appointment"}
            </h3>

            <form onSubmit={handleFormSubmit} className="mt-6 space-y-4">
              <div>
                <label className="block font-accent text-xs uppercase text-white/80 mb-1.5">Full Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formState.name}
                  onChange={handleFormChange}
                  className="w-full bg-white border border-transparent focus:border-white focus:ring-2 focus:ring-brand-orange-deep rounded-xl px-4 py-2.5 text-sm text-brand-black focus:outline-none transition-all placeholder-brand-gray/60"
                  placeholder="Enter Your Name"
                />
              </div>

              <div>
                <label className="block font-accent text-xs uppercase text-white/80 mb-1.5">Email Address</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formState.email}
                  onChange={handleFormChange}
                  className="w-full bg-white border border-transparent focus:border-white focus:ring-2 focus:ring-brand-orange-deep rounded-xl px-4 py-2.5 text-sm text-brand-black focus:outline-none transition-all placeholder-brand-gray/60"
                  placeholder="Enter Your Email"
                />
              </div>

              <div>
                <label className="block font-accent text-xs uppercase text-white/80 mb-1.5">Phone Number</label>
                <div className="flex gap-2 min-w-0">
                  <select
                    aria-label="Country calling code"
                    name="countryCode"
                    value={formState.countryCode}
                    onChange={handleFormChange}
                    className="shrink-0 w-[90px] bg-white border border-transparent focus:border-white focus:ring-2 focus:ring-brand-orange-deep rounded-xl px-2 py-2.5 text-sm text-brand-black focus:outline-none transition-all cursor-pointer"
                  >
                    <option value="+91" className="text-brand-black">+91 (IN)</option>
                    <option value="+1" className="text-brand-black">+1 (US)</option>
                    <option value="+44" className="text-brand-black">+44 (UK)</option>
                    <option value="+971" className="text-brand-black">+971 (UAE)</option>
                  </select>
                  <input
                    type="tel"
                    name="phone"
                    required
                    minLength={10}
                    maxLength={10}
                    inputMode="numeric"
                    value={formState.phone}
                    onChange={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "").slice(0, 10);
                      handleFormChange(e);
                    }}
                    className="flex-1 min-w-0 bg-white border border-transparent focus:border-white focus:ring-2 focus:ring-brand-orange-deep rounded-xl px-4 py-2.5 text-sm text-brand-black focus:outline-none transition-all placeholder-brand-gray/60"
                    placeholder="Phone Number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block font-accent text-xs uppercase text-white/80 mb-1.5">City</label>
                  <input
                    type="text"
                    name="city"
                    value={formState.city}
                    onChange={handleFormChange}
                    className="w-full min-w-0 bg-white border border-transparent focus:border-white focus:ring-2 focus:ring-brand-orange-deep rounded-xl px-4 py-2.5 text-sm text-brand-black focus:outline-none transition-all placeholder-brand-gray/60"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block font-accent text-xs uppercase text-white/80 mb-1.5">Age</label>
                  <input
                    type="number"
                    name="age"
                    required
                    value={formState.age}
                    onChange={handleFormChange}
                    className="w-full min-w-0 bg-white border border-transparent focus:border-white focus:ring-2 focus:ring-brand-orange-deep rounded-xl px-4 py-2.5 text-sm text-brand-black focus:outline-none transition-all placeholder-brand-gray/60"
                    placeholder="Age"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-6 w-full rounded-full bg-white text-brand-orange-deep shadow-lg py-3.5 px-4 text-center text-sm font-semibold transition-transform hover:-translate-y-0.5 active:scale-[0.98]"
              >
                {isSubmitting ? "Submitting..." : (buttonText || "Submit Request")}
              </button>
              {submitError && <p role="alert" className="mt-3 text-sm text-white">{submitError}</p>}
            </form>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
